import React from 'react';
import { Input } from '../../../../../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../../../components/ui/select';
import {
  FieldGroup,
  RepairFormGrid,
  RepairFormSectionTitle,
  TriStateCheckRow,
} from './spoolDetailFormUi';
import type { WeldingRepairEntryForm, WeldingRepairWorkType } from './weldingRepairEntry';

export function WeldingRepairEntrySection({
  value,
  onChange,
  repairNo,
  welderOptions,
}: {
  value: WeldingRepairEntryForm;
  onChange: (next: WeldingRepairEntryForm) => void;
  repairNo: number | null;
  welderOptions: { value: string; label: string }[];
}) {
  const set = <K extends keyof WeldingRepairEntryForm>(key: K, v: WeldingRepairEntryForm[K]) => {
    onChange({ ...value, [key]: v });
  };

  const isCutout = value.workType === 'cutout';
  const cycleLabel = repairNo != null ? `Welding ${repairNo}` : 'Repair weld';

  return (
    <div
      className="rounded-lg border-2 border-amber-500 p-4 space-y-1"
      aria-label="Repair weld entry"
    >
      <h4 className="text-[13px] font-bold uppercase tracking-[0.04em] text-[#92400E] mb-3">
        Repair entry — {cycleLabel}
      </h4>
      {/* <p className="text-xs text-amber-900/90 mb-3">
        No NCR is required. Choose <strong>repair</strong> (grind-out and re-weld) or{' '}
        <strong>complete cut-out</strong> (remove a section and re-weld). Record excavation and re-weld
        details below; use pass configuration for manual amps, volts, and time per pass.
      </p> */}

      <RepairFormSectionTitle noTopMargin>Repair type</RepairFormSectionTitle>
      <RepairFormGrid>
        <FieldGroup label="Work type" required>
          <Select
            value={value.workType}
            onValueChange={(v) => set('workType', v as WeldingRepairWorkType)}
          >
            <SelectTrigger className="h-9 text-[13px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="repair">Repair — grind out + re-weld</SelectItem>
              <SelectItem value="cutout">Complete cut-out + re-weld</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
        <FieldGroup label="Repair attempt">
          <Select value={value.repairAttempt} onValueChange={(v) => set('repairAttempt', v)}>
            <SelectTrigger className="h-9 text-[13px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="attempt1">1st attempt</SelectItem>
              <SelectItem value="attempt2">2nd attempt</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
        <FieldGroup label="Repair procedure ref">
          <Input
            className="h-9 text-[13px]"
            placeholder="Approved procedure #"
            value={value.repairProcedureRef}
            onChange={(e) => set('repairProcedureRef', e.target.value)}
          />
        </FieldGroup>
      </RepairFormGrid>

      <RepairFormSectionTitle>What failed</RepairFormSectionTitle>
      <RepairFormGrid>
        <FieldGroup label="Defect type" required>
          <Select value={value.defectType} onValueChange={(v) => set('defectType', v)}>
            <SelectTrigger className="h-9 text-[13px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="slag">Slag inclusion</SelectItem>
              <SelectItem value="porosity">Porosity</SelectItem>
              <SelectItem value="lof">Lack of fusion</SelectItem>
              <SelectItem value="lop">Lack of penetration</SelectItem>
              <SelectItem value="crack">Crack</SelectItem>
              <SelectItem value="root_conc">Root concavity</SelectItem>
              <SelectItem value="undercut">Undercut</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
        <FieldGroup label="Location in pipe / joint" required>
          <Input
            className="h-9 text-[13px]"
            placeholder="e.g. 6 o'clock, root zone, 1200 mm from spool end"
            value={value.defectLocation}
            onChange={(e) => set('defectLocation', e.target.value)}
          />
        </FieldGroup>
        <FieldGroup label="Defect length (mm)">
          <Input
            className="h-9 text-[13px]"
            inputMode="decimal"
            value={value.defectLengthMm}
            onChange={(e) => set('defectLengthMm', e.target.value)}
          />
        </FieldGroup>
        <FieldGroup label="Defect depth (mm)">
          <Input
            className="h-9 text-[13px]"
            inputMode="decimal"
            value={value.defectDepthMm}
            onChange={(e) => set('defectDepthMm', e.target.value)}
          />
        </FieldGroup>
        <FieldGroup label="Triggering NDT report (optional)">
          <Input
            className="h-9 text-[13px] bg-white"
            value={value.triggeringNdtReport}
            onChange={(e) => set('triggeringNdtReport', e.target.value)}
          />
        </FieldGroup>
        {/* <FieldGroup label="Linked NCR (optional)">
          <Input
            className="h-9 text-[13px] bg-white"
            placeholder="Leave blank if no NCR"
            value={value.linkedNcr}
            onChange={(e) => set('linkedNcr', e.target.value)}
          />
        </FieldGroup> */}
      </RepairFormGrid>

      <RepairFormSectionTitle>
        {isCutout ? 'Cut-out & excavation' : 'Excavation (grind-out)'}
      </RepairFormSectionTitle>
      <RepairFormGrid>
        <FieldGroup label={isCutout ? 'Cut-out length (mm)' : 'Excavation depth (mm)'}>
          <Input
            className="h-9 text-[13px]"
            inputMode="decimal"
            value={isCutout ? value.repairLengthMm : value.excavationDepthMm}
            onChange={(e) =>
              isCutout ? set('repairLengthMm', e.target.value) : set('excavationDepthMm', e.target.value)
            }
          />
        </FieldGroup>
        {!isCutout ? (
          <FieldGroup label="Repair length (mm)">
            <Input
              className="h-9 text-[13px]"
              inputMode="decimal"
              value={value.repairLengthMm}
              onChange={(e) => set('repairLengthMm', e.target.value)}
            />
          </FieldGroup>
        ) : null}
        <FieldGroup label="Excavation NDT method">
          <Select
            value={value.excavationNdtMethod}
            onValueChange={(v) => set('excavationNdtMethod', v)}
          >
            <SelectTrigger className="h-9 text-[13px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mpi">MPI</SelectItem>
              <SelectItem value="pt">PT (dye penetrant)</SelectItem>
              <SelectItem value="vt">VT (visual)</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
        <FieldGroup label="Excavation NDT result">
          <Select
            value={value.excavationNdtResult}
            onValueChange={(v) => set('excavationNdtResult', v)}
          >
            <SelectTrigger className="h-9 text-[13px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pass">Pass — defect fully removed</SelectItem>
              <SelectItem value="fail">Fail — further work needed</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
        <FieldGroup label="Preheat before re-weld (°C)">
          <Input
            className="h-9 text-[13px]"
            inputMode="decimal"
            value={value.excavationPreheatTempC}
            onChange={(e) => set('excavationPreheatTempC', e.target.value)}
          />
        </FieldGroup>
      </RepairFormGrid>
      <div className="mt-2 mb-1">
        <TriStateCheckRow
          tag="Repair"
          labels={{
            neutral: 'QC verified excavation — pending',
            pass: 'QC verified excavation complete before re-weld',
            fail: 'QC verified excavation — not accepted',
          }}
          state={value.excavationQcVerified}
          onCycle={() =>
            set(
              'excavationQcVerified',
              value.excavationQcVerified === 'neutral'
                ? 'pass'
                : value.excavationQcVerified === 'pass'
                  ? 'fail'
                  : 'neutral'
            )
          }
        />
      </div>

      <RepairFormSectionTitle>Re-weld execution</RepairFormSectionTitle>
      <RepairFormGrid>
        <FieldGroup label="Repaired by (welder)" required>
          {welderOptions.length > 0 ? (
            <Select
              value={value.repairedByWelderId || undefined}
              onValueChange={(v) => set('repairedByWelderId', v)}
            >
              <SelectTrigger className="h-9 text-[13px] bg-white">
                <SelectValue placeholder="Select welder" />
              </SelectTrigger>
              <SelectContent>
                {welderOptions.map((w) => (
                  <SelectItem key={w.value} value={w.value}>
                    {w.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              className="h-9 text-[13px]"
              placeholder="Welder name or id"
              value={value.repairedByWelderId}
              onChange={(e) => set('repairedByWelderId', e.target.value)}
            />
          )}
        </FieldGroup>
        <FieldGroup label="Preheat temp (°C)">
          <Input
            className="h-9 text-[13px]"
            inputMode="decimal"
            value={value.repairPreheatTempC}
            onChange={(e) => set('repairPreheatTempC', e.target.value)}
          />
        </FieldGroup>
        <FieldGroup label="Interpass temp (°C)">
          <Input
            className="h-9 text-[13px]"
            inputMode="decimal"
            value={value.repairInterpassTempC}
            onChange={(e) => set('repairInterpassTempC', e.target.value)}
          />
        </FieldGroup>
        <FieldGroup label="Visual after repair">
          <Select
            value={value.visualAfterRepair}
            onValueChange={(v) => set('visualAfterRepair', v as WeldingRepairEntryForm['visualAfterRepair'])}
          >
            <SelectTrigger className="h-9 text-[13px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pass">Pass</SelectItem>
              <SelectItem value="fail">Fail</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
        <FieldGroup label="Notes">
          <Input
            className="h-9 text-[13px]"
            value={value.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
        </FieldGroup>
      </RepairFormGrid>
    </div>
  );
}
